import { useCallback, useEffect, useState } from 'react'

import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import clsx from 'clsx'

import { CreateObjectFieldCard } from 'entities/kyrgyzstan/ui/create-object'

import { KyrgyzstanCreateObjectApi } from 'shared/api/kyrgyzstan/create-object'
import type { ApiFieldErrorMap } from 'shared/lib/form'
import { applyApiFieldErrors } from 'shared/lib/form'
import { ButtonInputValue } from 'shared/ui/button-input-value'
import { useApiErrorDialog } from 'shared/ui/dialog-bottom-sheet/lib'
import { InputWithLabel } from 'shared/ui/input-label'
import { Paper } from 'shared/ui/paper'
import { TextareaWithLabel } from 'shared/ui/textarea-label'

import type { CreateObjectDraftStepScreenProps } from '../../types'
import { CreateObjectStepLayout } from '../create-object-step-layout'

import { DescriptionStepSkeleton } from './ui/description-step-skeleton'

import {
  CREATE_OBJECT_DESCRIPTION_TEXT_MAX_LENGTH,
  CREATE_OBJECT_DESCRIPTION_TITLE_MAX_LENGTH,
  createObjectDescriptionFormDefaultValues,
  createObjectDescriptionFormResolver,
  mapCreateObjectDescriptionFormValuesToRequest,
  mapCreateObjectDescriptionResponseToFormValues,
} from './lib/description-step-form'
import type { CreateObjectDescriptionFormValues } from './lib/description-step-form'
import s from './styles.module.scss'

type DescriptionStepOperation = 'idle' | 'loading' | 'submitting'

const descriptionStepApiFieldMap: ApiFieldErrorMap<CreateObjectDescriptionFormValues> =
  {
    title: 'title',
    description: 'description',
    guests_count: 'capacity',
    capacity: 'capacity',
  }

export const DescriptionStep = ({
  draftId,
  layoutProps,
  mode = 'create',
  onNext,
}: CreateObjectDraftStepScreenProps) => {
  const { t } = useTranslation()
  const { showApiErrorDialog } = useApiErrorDialog()
  const [operation, setOperation] = useState<DescriptionStepOperation>('idle')
  const {
    control,
    formState: { errors, isDirty, isValid },
    handleSubmit,
    register,
    reset,
    setError,
  } = useForm<CreateObjectDescriptionFormValues>({
    defaultValues: createObjectDescriptionFormDefaultValues,
    mode: 'onChange',
    resolver: createObjectDescriptionFormResolver,
  })

  useEffect(() => {
    const abortController = new AbortController()

    const fetchDescription = async () => {
      setOperation('loading')

      try {
        const { data } = await KyrgyzstanCreateObjectApi.getDescriptionStep(
          draftId,
          abortController.signal,
        )

        reset(mapCreateObjectDescriptionResponseToFormValues(data.data))
      } catch (error) {
        if (!abortController.signal.aborted) {
          showApiErrorDialog(error)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setOperation('idle')
        }
      }
    }

    fetchDescription()

    return () => {
      abortController.abort()
    }
  }, [draftId, reset, showApiErrorDialog])

  const handleSubmitDescription = useCallback(
    async (formValues: CreateObjectDescriptionFormValues) => {
      setOperation('submitting')
      let shouldGoNext = false

      try {
        await KyrgyzstanCreateObjectApi.saveDescriptionStep(
          draftId,
          mapCreateObjectDescriptionFormValuesToRequest(formValues),
        )

        shouldGoNext = true
      } catch (error) {
        if (!applyApiFieldErrors(error, setError, descriptionStepApiFieldMap)) {
          showApiErrorDialog(error)
        }
      } finally {
        setOperation('idle')
      }

      if (shouldGoNext) {
        onNext()
      }
    },
    [draftId, onNext, setError, showApiErrorDialog],
  )

  const handleContinue = useCallback(() => {
    if (operation !== 'idle') {
      return
    }

    void handleSubmit(handleSubmitDescription)()
  }, [handleSubmit, handleSubmitDescription, operation])

  const isEditModePristine = mode === 'edit' && !isDirty
  const isContinueDisabled =
    !isValid || operation !== 'idle' || isEditModePristine
  const isFormDisabled = operation !== 'idle'

  return (
    <CreateObjectStepLayout
      {...layoutProps}
      continueDisabled={isContinueDisabled}
      continueLoading={operation === 'submitting'}
      onContinue={handleContinue}
      title={t('kyrgyzstan_block.create_object.steps.description.title')}
    >
      <div className={clsx(s.container, 'container')}>
        {operation === 'loading' ? (
          <DescriptionStepSkeleton />
        ) : (
          <>
            <CreateObjectFieldCard
              title={t(
                'kyrgyzstan_block.create_object.steps.description.object_name',
              )}
            >
              <InputWithLabel
                variant="secondary"
                label={t(
                  'kyrgyzstan_block.create_object.steps.description.object_placeholder',
                )}
                maxLength={CREATE_OBJECT_DESCRIPTION_TITLE_MAX_LENGTH}
                disabled={isFormDisabled}
                error={errors.title}
                {...register('title')}
              />
            </CreateObjectFieldCard>

            <CreateObjectFieldCard
              title={t(
                'kyrgyzstan_block.create_object.steps.description.title',
              )}
            >
              <TextareaWithLabel
                variant="secondary"
                label={t(
                  'kyrgyzstan_block.create_object.steps.description.title',
                )}
                maxLength={CREATE_OBJECT_DESCRIPTION_TEXT_MAX_LENGTH}
                showCounter
                disabled={isFormDisabled}
                error={errors.description}
                {...register('description')}
              />
            </CreateObjectFieldCard>

            <Paper
              padding={[12, 16]}
              className={clsx(isFormDisabled && s.disabledField)}
            >
              <Controller
                control={control}
                name="capacity"
                render={({ field }) => (
                  <ButtonInputValue
                    title={t(
                      'kyrgyzstan_block.create_object.steps.description.capacity',
                    )}
                    subTitle={t(
                      'kyrgyzstan_block.create_object.steps.description.capacity_description',
                    )}
                    value={field.value}
                    minNumber={1}
                    maxNumber={Number.MAX_SAFE_INTEGER}
                    error={errors.capacity}
                    onChange={field.onChange}
                  />
                )}
              />
            </Paper>
          </>
        )}
      </div>
    </CreateObjectStepLayout>
  )
}
